import { colors, mq } from './config'
import { PaperScope, Point, Raster, CompoundPath, Group, Size } from 'paper'
import { rectangleOverlap, random, clamp } from './utils/math'
import { shapesLayout } from 'shapes-layout'
import { TimelineLite, TweenLite, Power2 } from 'gsap'
import bus from './bus'
import defs from '../../includes/shape/shape.json'
import times from 'lodash/times'
import UI from './utils/UI'
import defer from 'lodash/defer'

const textureBasePath = '/wp-content/themes/kaliber-fair-trade-original/assets/images/'
const textureUrl = textureBasePath + 'shape-texture.png'
const yMapHeight = 3000

UI({
  component: '.js-shapesBg'
}, ({ component }) => {
  const canvas = component
  const scope = new PaperScope()
  const selectedLayout = component.getAttribute('data-shape-layout')
  const layout = shapesLayout[selectedLayout || 0]

  if (!layout) return
  const shapes = layout.filter(item => item.shape)
  const insets = layout.filter(item => item.inset)
  const insetUrls = insets.map(i => `${textureBasePath}asia/${i.inset}.jpg`)

  scope.setup(canvas)

  Promise.all([windowLoaded(), ...rastersLoaded([textureUrl, ...insetUrls])])
    .then(([_, raster, ...insetRasters]) => {
      setPaperSize(scope, canvas)
      const preventShapes = getPreventShapes()
      const windowHeight = window.innerHeight
      const windowScrollY = window.scrollY
      const windowWidth = window.innerWidth
      raster.visible = true

      // Fill canvas with shapes & insets in y direction
      const tiledShapes = times(Math.ceil(canvas.height / yMapHeight)).reduce((res, index) => {
        return [...res, ...itemsWithOffset(shapes, index * 100)]
      }, [])

      const tiledInsetObjects = times(Math.ceil(canvas.height / yMapHeight)).reduce((res, index) => {
        return [...res, ...itemsWithOffset(insets, index * 100)]
      }, [])

      const tiledInsetObjectsWithRasters = tiledInsetObjects.map((item, index, arr) => {
        const insetUrlIndex = index > insetUrls.length - 1 ? index % insetUrls.length : index
        return { ...item, raster: new Raster(insetUrls[insetUrlIndex]) }
      })

      const drawnShapes = tiledShapes.map(shape => drawShape(shape, raster, canvas, preventShapes))
      const drawnInsets = tiledInsetObjectsWithRasters.map(insetObject => drawInset(insetObject, canvas, preventShapes))
      const allItems = drawnShapes.concat(drawnInsets)

      bus.on('window-resize', _ => handleResize(allItems, raster, canvas, scope))

      const itemsToAnimateOnLoad = allItems.reduce((res, item) => {
        if (item.position.y <= windowHeight + windowScrollY && item.position.y + item.bounds.height >= windowScrollY && item.visible) {
          res.push(item)
        }
        return res
      }, [])
      const tl = new TimelineLite({ paused: true })
      animateOnLoad(itemsToAnimateOnLoad, windowWidth, tl)
      scope.view.draw()
      defer(_ => tl.paused(false))
    })
    .catch(err => console.error(err))
})

function animateOnLoad(itemsToAnimateOnLoad, windowWidth, tl) {
  tl.timeScale(clamp(itemsToAnimateOnLoad.length / 1.5, 2.5, 5))
  itemsToAnimateOnLoad.forEach((item, index) => {
    tl.add(getItemTween(item, windowWidth), index > 0 ? '-=0.35' : '0')
  })
  tl.call(_ => bus.emit('shapes-bg-ready'), null, null, tl.totalDuration() * 0.5)
}

function getItemTween(item, windowWidth) {
  const startX = item.position.x <= windowWidth / 2 ? -item.bounds.width : windowWidth + item.bounds.width
  const startY = random(item.position.y - 150, item.position.y + 150)
  const startRotation = random(item.rotation + 20, item.rotation + 40) * random([-1, 1])
  const position = { x: startX, y: startY, rotation: startRotation }
  const endRotation = item.rotation

  return TweenLite.fromTo(
    position,
    0.7,
    {
      x: startX,
      y: startY,
      rotation: startRotation
    },
    {
      x: item.position.x,
      y: item.position.y,
      rotation: endRotation,
      ease: Power2.easeOut,
      onUpdate: _ => {
        item.setPosition(position.x, position.y)
        item.rotate(position.rotation - item.rotation)
      }
    })
}

function handleResize(items, raster, canvas, scope) {
  const preventShapes = getPreventShapes()
  setPaperSize(scope, canvas)
  items.forEach(item => setWorldPosition(item, canvas, preventShapes))
}

function drawShape(s, raster, canvas, preventShapes) {
  /* note: order of statements is important */
  const clipPath = getPath(s)
  const pattern = raster.clone()
  const fill = clipPath.clone()
  const overlay = getOverlay(s)

  pattern.blendMode = 'overlay'
  pattern.opacity = 0.7

  // Tile pattern
  const patternCopy1 = pattern.clone().translate(new Point(0, pattern.height))
  const patternCopy2 = pattern.clone().translate(new Point(pattern.width, 0))
  const patternCopy3 = pattern.clone().translate(new Point(pattern.width, pattern.height))
  fill.fillColor = colors[s.color]
  const group = new Group({
    children: [clipPath, fill, pattern, patternCopy1, patternCopy2, patternCopy3, overlay],
    clipped: true,
    name: `${s.shape}_${s.color}`,
    data: {
      localPosition: s.position
    },
    applyMatrix: false,
    blendMode: 'multiply'
  })

  return setWorldPosition(group, canvas, preventShapes)
}

function drawInset(inset, canvas, preventShapes) {
  const item = inset.raster
  item.set({
    name: inset.inset,
    data: {
      localPosition: inset.position
    },
    applyMatrix: false
  })

  item.scale(inset.scale || 1)
  item.rotate(inset.rotate || 0)

  return setWorldPosition(item, canvas, preventShapes)
}

function setWorldPosition (item, canvas, preventShapes) {

  item.setPosition(mapToWorldCoords(item.data.localPosition, canvas))

  // hide if item overlaps with an element with the class "js-preventShape"
  const isHidden = preventShapes.reduce((res, preventShape) => (res || rectangleOverlap(preventShape, item.bounds)), false)
  item.visible = !isHidden
  return item
}

function getOverlay(s) {
  if (s.overlay) {
    const fill = getPath(s.overlay)
    fill.fillColor = '#fff'
    fill.opacity = 0.34
    fill.translate(s.overlay.translate)
    fill.blendMode = 'overlay'
    return fill
  }
}

function getPath (s) {
  const clipPath = new CompoundPath(defs[s.shape].path)
  const scale = s.scale || 1
  if (s.flipY || s.flipX) {
    clipPath.scale(scale * (s.flipX ? -1 : 1), scale * (s.flipY ? -1 : 1))
  } else {
    clipPath.scale(scale)
  }
  clipPath.rotate(s.rotate || 0)
  return clipPath
}

function mapToWorldCoords(point, canvas) {
  const x = calcXWorldCoord(point.x, canvas)
  const y = (point.y / 100) * yMapHeight
  return new Point(x, y)
}

function calcXWorldCoord (origX, canvas) {
  const scaleFactor = mq.xl.matches
    ? 1
    : mq.lg.matches
      ? 1.2
      : mq.md.matches
        ? 1.4
        : mq.sm.matches
          ? 1.6
          : 1.75

  const scaledCanvasWidth = canvas.width * scaleFactor
  const halfCanvasOverflow = canvas.width * (scaleFactor - 1) / 2
  return ((origX / 100) * scaledCanvasWidth) - halfCanvasOverflow
}

function setPaperSize(scope, canvas) {
  const w = document.body.clientWidth
  const h = document.body.clientHeight
  scope.view.viewSize = new Size(w, h)
  canvas.width = w
  canvas.height = h
}

function windowLoaded () {
  return new Promise(resolve => { window.onload = resolve })
}

function rastersLoaded (urls) {
  const res = urls.map(url => {
    const raster = new Raster(url)
    raster.visible = false
    return preloadRaster(raster)
  })

  return res
}

function preloadRaster (raster) {
  return new Promise((resolve, reject) => {
    raster.onLoad = _ => resolve(raster)
    raster.onError = err => reject(err)
  })
}

function itemsWithOffset (items, offset) {
  return items.map(s => ({ ...s, position: new Point(s.position.x, s.position.y + offset) }))
}

function getPreventShapes () {
  return [...document.querySelectorAll('.js-preventShapes')].map(el => {
    const eBCR = el.getBoundingClientRect()
    return { x: eBCR.left, width: eBCR.width, height: eBCR.height, y: eBCR.top + window.scrollY }
  })
}
